/**
 * PerformanceContributors Component
 * Breakdown of performance score by factor with visual bars
 */

import React from 'react';
import { clsx } from 'clsx';
import { Card } from '../common';
import type { PerformanceFactor } from '../../types';
import { getScoreColor } from '../../types';

export interface PerformanceContributorsProps {
  factors: PerformanceFactor[];
  title?: string;
  className?: string;
  isLoading?: boolean;
}

export const PerformanceContributors: React.FC<PerformanceContributorsProps> = ({
  factors,
  title = 'Score Breakdown',
  className,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <div className="animate-pulse">
          <div className="h-5 bg-stone-200 rounded w-32 mb-6" />
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="mb-4">
              <div className="h-4 bg-stone-200 rounded w-40 mb-2" />
              <div className="h-3 bg-stone-200 rounded w-full mb-1" />
              <div className="h-3 bg-stone-200 rounded w-2/3" />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (!factors || factors.length === 0) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-800 mb-4">{title}</h3>
        <p className="text-stone-400">No factor data available</p>
      </Card>
    );
  }

  return (
    <Card className={clsx('p-6', className)}>
      <h3 className="text-lg font-semibold text-stone-800 mb-6">{title}</h3>

      <div className="space-y-5">
        {factors.map((factor) => (
          <div key={factor.factor}>
            {/* Factor header */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="font-medium text-stone-700">{factor.name}</span>
                <span className="text-xs text-stone-400">
                  ({Math.round(factor.weight * 100)}% weight)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="text-lg font-bold"
                  style={{ color: getScoreColor(factor.score) }}
                >
                  {Math.round(factor.score)}
                </span>
                <span className="text-sm text-stone-400">/100</span>
              </div>
            </div>

            {/* Progress bar */}
            <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${factor.score}%`,
                  backgroundColor: getScoreColor(factor.score),
                }}
              />
            </div>

            {/* Weighted contribution */}
            <div className="mt-1 flex items-center justify-between text-xs">
              <span className="text-stone-500">{factor.description}</span>
              <span className="text-stone-400">
                +{factor.weightedScore.toFixed(1)} to overall
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Total */}
      <div className="mt-6 pt-4 border-t border-stone-200">
        <div className="flex items-center justify-between">
          <span className="font-medium text-stone-700">Total Score</span>
          <span className="text-xl font-bold text-stone-900">
            {Math.round(factors.reduce((sum, f) => sum + f.weightedScore, 0))}
          </span>
        </div>
      </div>
    </Card>
  );
};

export default PerformanceContributors;
